// src/pages/tournamentDetails/components/BlindStructureCard.js
import React, { useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";

function renumberBlindLevels(items) {
  let nextLevel = 1;

  return (Array.isArray(items) ? items : []).map((item) => {
    if (item?.type === "level") {
      return {
        ...item,
        level: nextLevel++,
      };
    }

    return { ...item };
  });
}

export default function BlindStructureCard({
  blinds,
  setBlinds,
  isAdmin,
  savingStructure,
  setSavingStructure,
  tournamentId,
  db,
  serverTimestamp,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const normalizedBlinds = useMemo(() => {
    return renumberBlindLevels(blinds);
  }, [blinds]);

  const commitBlinds = (updater) => {
    setBlinds((prev) => {
      const next =
        typeof updater === "function" ? updater(Array.isArray(prev) ? prev : []) : updater;

      return renumberBlindLevels(next);
    });
  };

  const addLevel = () => {
    commitBlinds((prev) => [
      ...prev,
      {
        type: "level",
        level: prev.filter((b) => b?.type === "level").length + 1,
        sb: 100,
        bb: 200,
        ante: 0,
        minutes: 20,
      },
    ]);
  };

  const addBreak = () => {
    commitBlinds((prev) => [
      ...prev,
      { type: "break", label: "Break", minutes: 10 },
    ]);
  };

  const removeBlindItem = (idx) => {
    commitBlinds((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateBlindItem = (idx, patch) => {
    commitBlinds((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    );
  };

  const renumberLevels = () => {
    commitBlinds((prev) => prev);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    commitBlinds((prev) => {
      const list = [...prev];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return list;
    });
  };

  const moveItemUp = (idx) => {
    if (idx <= 0) return;
    moveItem(idx, idx - 1);
  };

  const moveItemDown = (idx) => {
    if (idx >= normalizedBlinds.length - 1) return;
    moveItem(idx, idx + 1);
  };

  const handleDragStart = (idx) => {
    if (!isAdmin) return;
    setDragIndex(idx);
    setDragOverIndex(idx);
  };

  const handleDragOver = (e, idx) => {
    if (!isAdmin) return;
    e.preventDefault();
    if (dragOverIndex !== idx) {
      setDragOverIndex(idx);
    }
  };

  const handleDrop = (idx) => {
    if (!isAdmin) return;
    if (dragIndex == null) return;

    moveItem(dragIndex, idx);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const saveStructure = async () => {
    if (!isAdmin) return;

    setSavingStructure(true);
    try {
      const tRef = doc(db, "tournaments", tournamentId);
      await updateDoc(tRef, {
        blinds: renumberBlindLevels(normalizedBlinds),
        updatedAt: serverTimestamp(),
      });
      alert("Blind structure saved.");
    } catch (err) {
      console.error("Save failed:", err);
      alert("Save failed. Check permissions.");
    }
    setSavingStructure(false);
  };

  return (
    <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-[#B87333]">Blind Structure</h3>
          {isAdmin ? (
            <p className="text-zinc-400 text-sm mt-1">
              Drag using the handle on the left, or use Move Up / Move Down.
            </p>
          ) : null}
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={addLevel}
              className="px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
            >
              + Add Level
            </button>
            <button
              onClick={addBreak}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
            >
              + Add Break
            </button>
            <button
              onClick={renumberLevels}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
            >
              Renumber
            </button>
            <button
              onClick={saveStructure}
              disabled={savingStructure}
              className={`px-4 py-2 rounded font-semibold ${
                savingStructure
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {savingStructure ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>

      {normalizedBlinds.length === 0 ? (
        <p className="text-zinc-400">No blind structure yet.</p>
      ) : (
        <div className="space-y-3">
          {normalizedBlinds.map((item, idx) => {
            const isDragging = dragIndex === idx;
            const isDropTarget = dragOverIndex === idx && dragIndex !== idx;

            return (
              <div
                key={`${item.type}-${item.level || item.label || "item"}-${idx}`}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={() => handleDrop(idx)}
                className={`rounded border bg-zinc-950 transition ${
                  isDragging
                    ? "border-[#B87333] opacity-60"
                    : isDropTarget
                    ? "border-blue-500"
                    : "border-zinc-800"
                }`}
              >
                <div className="flex items-stretch">
                  {isAdmin ? (
                    <div className="border-r border-zinc-800 flex items-center justify-center px-3 bg-zinc-900/70">
                      <button
                        type="button"
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragEnd={handleDragEnd}
                        className="h-full min-h-[88px] flex items-center justify-center text-zinc-400 hover:text-zinc-200 cursor-grab active:cursor-grabbing"
                        title="Drag to reorder"
                        aria-label="Drag to reorder"
                      >
                        <span className="text-xl leading-none select-none">⋮⋮</span>
                      </button>
                    </div>
                  ) : null}

                  <div className="flex-1 p-3">
                    {item.type === "level" ? (
                      <div className="grid gap-3 md:grid-cols-8 items-end">
                        <div className="md:col-span-1">
                          <p className="text-zinc-400 text-sm">Level</p>
                          <p className="text-zinc-100 font-semibold">{item.level}</p>
                        </div>

                        <div>
                          <label className="block text-zinc-400 text-sm">SB</label>
                          <input
                            type="number"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.sb}
                            onChange={(e) =>
                              updateBlindItem(idx, { sb: Number(e.target.value) })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        <div>
                          <label className="block text-zinc-400 text-sm">BB</label>
                          <input
                            type="number"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.bb}
                            onChange={(e) =>
                              updateBlindItem(idx, { bb: Number(e.target.value) })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        <div>
                          <label className="block text-zinc-400 text-sm">Ante</label>
                          <input
                            type="number"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.ante || 0}
                            onChange={(e) =>
                              updateBlindItem(idx, { ante: Number(e.target.value) })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        <div>
                          <label className="block text-zinc-400 text-sm">Minutes</label>
                          <input
                            type="number"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.minutes}
                            onChange={(e) =>
                              updateBlindItem(idx, { minutes: Number(e.target.value) })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        {isAdmin ? (
                          <div className="md:col-span-3 flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => moveItemUp(idx)}
                              disabled={idx === 0}
                              className={`px-3 py-2 rounded font-semibold ${
                                idx === 0
                                  ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                              }`}
                            >
                              ↑ Move Up
                            </button>

                            <button
                              type="button"
                              onClick={() => moveItemDown(idx)}
                              disabled={idx === normalizedBlinds.length - 1}
                              className={`px-3 py-2 rounded font-semibold ${
                                idx === normalizedBlinds.length - 1
                                  ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                              }`}
                            >
                              ↓ Move Down
                            </button>

                            <button
                              type="button"
                              onClick={() => removeBlindItem(idx)}
                              className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                            >
                              Remove
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="grid gap-3 md:grid-cols-8 items-end">
                        <div className="md:col-span-2">
                          <label className="block text-zinc-400 text-sm">Break Label</label>
                          <input
                            type="text"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.label || "Break"}
                            onChange={(e) =>
                              updateBlindItem(idx, { label: e.target.value })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        <div>
                          <label className="block text-zinc-400 text-sm">Minutes</label>
                          <input
                            type="number"
                            className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                            value={item.minutes}
                            onChange={(e) =>
                              updateBlindItem(idx, { minutes: Number(e.target.value) })
                            }
                            disabled={!isAdmin}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <p className="text-zinc-100 font-semibold">Break</p>
                          <p className="text-zinc-400 text-sm">
                            Break will auto-advance when time ends.
                          </p>
                        </div>

                        {isAdmin ? (
                          <div className="md:col-span-3 flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => moveItemUp(idx)}
                              disabled={idx === 0}
                              className={`px-3 py-2 rounded font-semibold ${
                                idx === 0
                                  ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                              }`}
                            >
                              ↑ Move Up
                            </button>

                            <button
                              type="button"
                              onClick={() => moveItemDown(idx)}
                              disabled={idx === normalizedBlinds.length - 1}
                              className={`px-3 py-2 rounded font-semibold ${
                                idx === normalizedBlinds.length - 1
                                  ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                              }`}
                            >
                              ↓ Move Down
                            </button>

                            <button
                              type="button"
                              onClick={() => removeBlindItem(idx)}
                              className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                            >
                              Remove
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}